import { useState } from 'react';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Button, Dialog, EmptyState, IconButton, Skeleton } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { money, parseMoney } from '../lib/format.ts';
import { useAction, useSave } from './manage/common.tsx';

/**
 * Lead scoring in the CRM: the chip on each opportunity, the reasons behind a score, and the
 * dialog where a manager says what makes an opportunity worth chasing.
 */

export type Band = 'hot' | 'warm' | 'cold';
export const SCORE_KINDS = ['source', 'customer', 'company', 'email', 'phone', 'value_at_least', 'done_at_least', 'priority_at_least', 'engaged', 'quiet_days'] as const;
export type ScoreKind = (typeof SCORE_KINDS)[number];
export interface ScoreRule {
  kind: ScoreKind;
  source: string | null;
  amount: number;
  number: number;
  points: number;
}
export interface Score {
  score: number;
  band: Band;
  reasons: (ScoreRule & { rule: string })[];
}
interface Rules {
  rules: (ScoreRule & { id: string })[];
  suggested: ScoreRule[];
  hot: number;
  warm: number;
  marketing: boolean;
}

const BAND_ICON: Record<Band, IconName> = { hot: 'flame', warm: 'trendUp', cold: 'circle' };
const SOURCES = ['phone', 'walk_in', 'web', 'email', 'referral', 'event', 'social', 'other'] as const;

export function ScoreChip({ score, band }: { score: number; band: Band }) {
  const label = t('score.chip_label', { n: score, band: t(`score.band_${band}` as MessageKey) });
  return (
    <span className={`score-chip score-${band}`} title={label} aria-label={label}>
      <Icon name={BAND_ICON[band]} size={12} />
      <span className="tabular">{score}</span>
    </span>
  );
}

/** What a rule checks, in words. */
export function ruleText(r: Pick<ScoreRule, 'kind' | 'source' | 'amount' | 'number'>) {
  switch (r.kind) {
    case 'source':
      return t('score.r_source', { source: r.source ? t(`crm.src_${r.source}` as MessageKey) : '…' });
    case 'value_at_least':
      return t('score.r_value_at_least', { amount: money(r.amount) });
    case 'done_at_least':
      return tn('score.r_done_at_least', r.number);
    case 'priority_at_least':
      return tn('score.r_priority_at_least', r.number);
    case 'quiet_days':
      return tn('score.r_quiet_days', r.number);
    default:
      return t(`score.r_${r.kind}` as MessageKey);
  }
}

const signed = (n: number) => (n > 0 ? `+${n}` : String(n));

/** The score of one opportunity, with the rules that gave it. */
export function ScorePanel({ score }: { score: Score }) {
  return (
    <section className="panel" aria-labelledby="crm-score">
      <div className="panel-head">
        <h2 id="crm-score">{t('score.title')}</h2>
        <ScoreChip score={score.score} band={score.band} />
      </div>
      <div className="score-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={score.score} aria-label={t('score.title')}>
        <span className={`score-meter-fill score-${score.band}`} style={{ width: `${score.score}%` }} />
      </div>
      {score.reasons.length ? (
        <ul className="score-reasons">
          {score.reasons.map((r) => (
            <li key={r.rule}>
              <span>{ruleText(r)}</span>
              <span className={`tabular strong ${r.points < 0 ? 'neg' : 'pos'}`}>{signed(r.points)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted small">{t('score.no_reasons')}</p>
      )}
    </section>
  );
}

type Row = ScoreRule & { key: string; amountText: string };
const toRow = (r: ScoreRule, i: number): Row => ({ ...r, key: `r${i}-${r.kind}`, amountText: r.amount ? String(r.amount / 100) : '' });
const needsNumber = (k: ScoreKind) => k === 'done_at_least' || k === 'priority_at_least' || k === 'quiet_days';

export function ScoringDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { data } = useAction<Rules>('crm.score_rules', {});
  const [rows, setRows] = useState<Row[] | null>(null);
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const list = rows ?? data?.rules.map(toRow) ?? null;
  const edit = (i: number, patch: Partial<Row>) => list && setRows(list.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const problem = (r: Row) =>
    r.kind === 'source' && !r.source
      ? t('score.e_source')
      : r.kind === 'value_at_least' && !(parseMoney(r.amountText) ?? 0)
        ? t('score.e_amount')
        : needsNumber(r.kind) && r.number < 1
          ? t('score.e_number')
          : !r.points
            ? t('score.e_points')
            : null;
  const save = async () => {
    if (!list) return;
    setTried(true);
    if (list.some(problem)) return;
    const body = list.map((r) => ({ kind: r.kind, source: r.source, amount: r.kind === 'value_at_least' ? (parseMoney(r.amountText) ?? 0) : 0, number: r.number, points: r.points }));
    if (await run(() => action('crm.save_score_rules', { rules: body }), t('score.saved'))) onDone();
  };
  return (
    <Dialog
      title={t('score.rules_title')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" icon="plus" disabled={!list} onClick={() => list && setRows([...list, toRow({ kind: 'source', source: 'referral', amount: 0, number: 0, points: 10 }, list.length + Date.now())])}>
            {t('score.add_rule')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!list} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      {!list || !data ? (
        <Skeleton h={240} />
      ) : (
        <div className="form-stack">
          <p className="muted small">{t('score.rules_hint', { hot: data.hot, warm: data.warm })}</p>
          <div className="score-bands" aria-hidden="true">
            <ScoreChip score={data.hot} band="hot" />
            <span className="muted small">{t('score.band_hot_hint', { n: data.hot })}</span>
            <ScoreChip score={data.warm} band="warm" />
            <span className="muted small">{t('score.band_warm_hint', { n: data.warm })}</span>
          </div>
          {!list.length ? (
            <EmptyState
              icon="flame"
              title={t('score.empty')}
              body={t('score.empty_hint')}
              action={
                <Button variant="primary" icon="sparkle" onClick={() => setRows(data.suggested.map(toRow))}>
                  {t('score.use_suggested')}
                </Button>
              }
            />
          ) : (
            <ol className="score-rules">
              {list.map((r, i) => {
                const err = tried ? problem(r) : null;
                return (
                  <li key={r.key} className={err ? 'invalid' : ''}>
                    <select className="input" aria-label={t('score.f_kind')} value={r.kind} onChange={(e) => edit(i, { kind: e.target.value as ScoreKind, number: needsNumber(e.target.value as ScoreKind) && !r.number ? (e.target.value === 'quiet_days' ? 14 : 1) : r.number })}>
                      {SCORE_KINDS.filter((k) => k !== 'engaged' || data.marketing || r.kind === 'engaged').map((k) => (
                        <option key={k} value={k}>
                          {t(`score.k_${k}` as MessageKey)}
                        </option>
                      ))}
                    </select>
                    {r.kind === 'source' ? (
                      <select className="input" aria-label={t('score.f_source')} value={r.source ?? ''} onChange={(e) => edit(i, { source: e.target.value || null })}>
                        <option value="">{t('score.choose_source')}</option>
                        {SOURCES.map((s) => (
                          <option key={s} value={s}>
                            {t(`crm.src_${s}` as MessageKey)}
                          </option>
                        ))}
                      </select>
                    ) : r.kind === 'value_at_least' ? (
                      <input className="input tabular" inputMode="decimal" aria-label={t('score.f_amount')} placeholder={t('score.f_amount')} value={r.amountText} onChange={(e) => edit(i, { amountText: e.target.value })} />
                    ) : needsNumber(r.kind) ? (
                      <span className="score-num">
                        <input
                          className="input tabular"
                          inputMode="numeric"
                          aria-label={t(`score.n_${r.kind}` as MessageKey)}
                          value={String(r.number || '')}
                          onChange={(e) => edit(i, { number: Math.min(3650, Number(e.target.value.replace(/\D/g, '') || 0)) })}
                        />
                        <span className="muted small">{t(`score.n_${r.kind}` as MessageKey)}</span>
                      </span>
                    ) : (
                      <span />
                    )}
                    <span className="score-points">
                      <IconButton icon="minus" label={t('score.less')} onClick={() => edit(i, { points: Math.max(-100, r.points - 5) })} />
                      <input
                        className={`input tabular ${r.points < 0 ? 'neg' : ''}`}
                        inputMode="numeric"
                        aria-label={t('score.f_points')}
                        value={signed(r.points)}
                        onChange={(e) => {
                          const v = e.target.value.trim();
                          const n = Number(v.replace(/[^\d-]/g, '') || 0);
                          edit(i, { points: Math.max(-100, Math.min(100, Number.isFinite(n) ? n : 0)) });
                        }}
                      />
                      <IconButton icon="plus" label={t('score.more')} onClick={() => edit(i, { points: Math.min(100, r.points + 5) })} />
                    </span>
                    <IconButton icon="trash" label={t('score.remove')} onClick={() => setRows(list.filter((_, j) => j !== i))} />
                    {err ? (
                      <span className="field-msg score-err" role="alert">
                        {err}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          )}
          {list.length ? <p className="muted small">{tn('score.n_rules', list.length)}</p> : null}
        </div>
      )}
    </Dialog>
  );
}
